/**
 * readApprovalContent — resolve an approval document's `content` blob into the
 * fields the Control Center renders.
 *
 * 🔴 WHY THIS EXISTS AS ITS OWN MODULE (2026-09-22, John's CC review item #9).
 *
 * The nurture approval modal rendered COMPLETELY EMPTY for a paying client: no
 * recipient, no message body, no date. He would have been approving a text message
 * he could not read — and the Approve button worked fine, so nothing looked broken
 * from our side.
 *
 * The cause was not the modal. Every field was read under a name the WRITER never
 * writes. Measured against the real documents in `clients/{id}/pendingApprovals`:
 *
 *     writer emits  content.body          ·  reader read  content.message
 *     writer emits  content.contactName   ·  reader read  content.recipientName
 *     writer emits  NO scheduledDate      ·  reader read  content.scheduledDate
 *
 * That is the identity-contract class this repo keeps paying for: two halves of one
 * feature each internally consistent, disagreeing about a field name, with nothing
 * comparing them. A blank string renders as nothing, so absence is SILENT — there is
 * no error, no empty-state, no log line. It looked like a styling bug for days.
 *
 * ⚠️ THE MODULE LIVES IN `app/shared` ON PURPOSE. CI runs vitest ONLY in app/shared
 * (see pr.yml "Shared logic tests"), so a test sitting beside the page component
 * would never run in CI and would not be a gate at all.
 *
 * ⚠️ WHEN A WRITER ADDS A FIELD, ADD ITS NAME HERE — do not rename the writer to
 * match the reader. Older documents are already in Firestore under the old names and
 * would go blank the moment the writer changed.
 */

/** Narrow an unknown to a non-empty string, else undefined. */
const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v ? v : undefined

export interface ApprovalContentFields {
  message?: string
  recipientName?: string
  recipientBusiness?: string
  lastService?: string
  scheduledDate?: string
  attachmentUrl?: string
  // ── Ad cards (2026-10-05, Meta ads launch Phase 0) — written by
  //    backend agents/metaAdCard.ts. What the owner is agreeing to SPEND, who sees
  //    it and where a click goes. The ad writer stores `callToAction` and `imageUrl`;
  //    the Control Center read `cta` and `image`, so every real ad card showed the
  //    default "Learn More" button and no picture.
  adBudgetLine?: string
  adAudience?: string
  adWrittenFor?: string
  adDestination?: string
  adCta?: string
  adImage?: string
  // ── Media (2026-10-06, CPA Jake) — the social writer stores `imageUrl`, the video
  //    writer `videoUrl` + `thumbnailUrl`. The page read `image` / `thumbnail` and had
  //    no video field at all, so his Instagram post showed no picture and his Short
  //    showed a black box that could not play.
  image?: string
  thumbnail?: string
  videoUrl?: string
  // ── Replies (2026-10-15, CPA Jake: "no words … for the proposed response") — the
  //    Google review writer stores `originalReview` + `proposedReply`; the comment / DM
  //    writer `originalText` + `commenterName` + `proposedReply`. The page read
  //    `reviewText` + `proposedResponse`, so every reply card showed empty quotes.
  replyText?: string
  replyToText?: string
  replyToName?: string
  // ── Blog post (2026-10-27, CPA Jake: "I can't see the post at all") — the blog writer
  //    stores the article as `html` + `metaDescription`. The page mapped it to a social
  //    card reading `text`, so only the title and picture showed. Render the HTML through
  //    blogHtmlToMarkdown (approvals/blogBody.ts), never as markup.
  blogHtml?: string
  blogMetaDescription?: string
  // ── Post words (2026-10-27, CPA Jake: "the Google post for Nov 3 shows nothing on
  //    Preview"). The Google post writer stores its words as `postText` and its picture as
  //    `postPhotoUrl`; the page read `text` / `imageUrl`, so every gbp-post on file drew
  //    nothing. The social / library writers store `text`.
  postText?: string
}

/**
 * The accepted spellings, newest-writer-first. Each list is ordered by preference,
 * so a document carrying both wins on the CURRENT writer's name.
 */
export const APPROVAL_CONTENT_ALIASES = {
  // `body` is what the nurture writer emits; `message` is the older/mock name.
  message: ['body', 'message'],
  // `contactName` is what the nurture writer emits; `recipientName` is older/mock.
  recipientName: ['contactName', 'recipientName'],
  recipientBusiness: ['recipientBusiness'],
  lastService: ['lastService'],
  scheduledDate: ['scheduledDate'],
  attachmentUrl: ['attachmentUrl'],
  adBudgetLine: ['budgetLine'],
  adAudience: ['audience'],
  adWrittenFor: ['audienceWrittenFor'],
  adDestination: ['destinationUrl'],
  adCta: ['callToAction', 'cta'],
  adImage: ['imageUrl', 'image'],
  image: ['imageUrl', 'postPhotoUrl', 'image'],
  thumbnail: ['thumbnailUrl', 'thumbnail'],
  // `clipPublicUrl` / `publicUrl`: the long-form clip writer (2026-10-21, Barber Jake —
  // his Shorts showed no video). A clip that stored only `clipGsUri` is derived below.
  videoUrl: ['videoUrl', 'renderedUrl', 'playbackUrl', 'mediaUrl', 'clipPublicUrl', 'publicUrl'],
  replyText: ['proposedReply', 'proposedResponse'],
  replyToText: ['originalReview', 'originalText', 'reviewText'],
  replyToName: ['reviewerName', 'commenterName'],
  blogHtml: ['html'],
  blogMetaDescription: ['metaDescription'],
  postText: ['text', 'postText', 'caption'],
} as const satisfies Record<keyof ApprovalContentFields, readonly string[]>

/**
 * The public https URL of a `gs://bucket/path` object, or undefined. MIRRORS
 * `gsUriToPublicUrl` in backend approval/approvalArtifactGate.ts. A long-form clip
 * approval queued before 2026-10-21 stored only `clipGsUri`; its object is in the
 * public-read batch bucket, so this form plays.
 */
export function gsUriToPublicUrl(gsUri: unknown): string | undefined {
  if (typeof gsUri !== 'string' || !gsUri.startsWith('gs://')) return undefined
  const rest = gsUri.slice(5)
  const slash = rest.indexOf('/')
  if (slash <= 0 || slash === rest.length - 1) return undefined
  return `https://storage.googleapis.com/${rest.slice(0, slash)}/${rest.slice(slash + 1)}`
}

export function readApprovalContent(
  content: Record<string, unknown> | undefined | null,
): ApprovalContentFields {
  const c = content ?? {}
  const pick = (names: readonly string[]): string | undefined => {
    for (const n of names) {
      const v = str(c[n])
      if (v !== undefined) return v
    }
    return undefined
  }
  return {
    message: pick(APPROVAL_CONTENT_ALIASES.message),
    recipientName: pick(APPROVAL_CONTENT_ALIASES.recipientName),
    recipientBusiness: pick(APPROVAL_CONTENT_ALIASES.recipientBusiness),
    lastService: pick(APPROVAL_CONTENT_ALIASES.lastService),
    scheduledDate: pick(APPROVAL_CONTENT_ALIASES.scheduledDate),
    attachmentUrl: pick(APPROVAL_CONTENT_ALIASES.attachmentUrl),
    adBudgetLine: pick(APPROVAL_CONTENT_ALIASES.adBudgetLine),
    adAudience: pick(APPROVAL_CONTENT_ALIASES.adAudience),
    adWrittenFor: pick(APPROVAL_CONTENT_ALIASES.adWrittenFor),
    adDestination: pick(APPROVAL_CONTENT_ALIASES.adDestination),
    adCta: pick(APPROVAL_CONTENT_ALIASES.adCta),
    adImage: pick(APPROVAL_CONTENT_ALIASES.adImage),
    image: pick(APPROVAL_CONTENT_ALIASES.image),
    thumbnail: pick(APPROVAL_CONTENT_ALIASES.thumbnail),
    videoUrl: pick(APPROVAL_CONTENT_ALIASES.videoUrl) ?? gsUriToPublicUrl(c.clipGsUri),
    replyText: pick(APPROVAL_CONTENT_ALIASES.replyText),
    replyToText: pick(APPROVAL_CONTENT_ALIASES.replyToText),
    replyToName: pick(APPROVAL_CONTENT_ALIASES.replyToName),
    blogHtml: pick(APPROVAL_CONTENT_ALIASES.blogHtml),
    blogMetaDescription: pick(APPROVAL_CONTENT_ALIASES.blogMetaDescription),
    postText: pick(APPROVAL_CONTENT_ALIASES.postText),
  }
}

const GOOGLE_STARS: Record<string, number> = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 }

/**
 * The star rating of a review reply: the Google writer stores `starRating` (a number, or
 * Google's own word — 'FIVE'); older / mock documents `reviewerRating`. Undefined when
 * there is none (the card then shows no stars, never a guessed five).
 */
export function readApprovalRating(content: Record<string, unknown> | undefined | null): number | undefined {
  const c = content ?? {}
  for (const k of ['starRating', 'reviewerRating']) {
    const v = c[k]
    const n = typeof v === 'number' ? v : typeof v === 'string' ? (GOOGLE_STARS[v.toUpperCase()] ?? Number(v)) : NaN
    if (Number.isFinite(n) && n >= 1 && n <= 5) return Math.round(n)
  }
  return undefined
}

/**
 * The platforms a piece is for. The social and library writers store ONE platform as
 * `platform: 'instagram'`; the video writer stores `platforms: ['youtube']`; older docs
 * `targetPlatform`. Reading only `platforms` gave a social post NO platform, and the
 * preview modal — which picks its Facebook / Instagram / Google body by platform —
 * rendered completely blank for CPA Jake (2026-10-06).
 */
export function readApprovalPlatforms(content: Record<string, unknown> | undefined | null): string[] {
  const c = content ?? {}
  if (Array.isArray(c.platforms)) {
    const list = c.platforms.filter((p): p is string => typeof p === 'string' && p.length > 0)
    if (list.length > 0) return list
  }
  for (const k of ['platform', 'targetPlatform']) {
    const v = str(c[k])
    if (v) return [v]
  }
  return []
}

/** "0:32" from the video writer's `videoDurationSec` (or legacy `durationSeconds`). */
export function readApprovalVideoDuration(content: Record<string, unknown> | undefined | null): string | undefined {
  const c = content ?? {}
  for (const k of ['videoDurationSec', 'durationSeconds']) {
    const n = c[k]
    if (typeof n === 'number' && Number.isFinite(n) && n > 0) {
      const sec = Math.round(n)
      return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`
    }
  }
  return str(c.videoDuration)
}
